import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  PointOfSale as PosIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useVenue } from '../../../../context/VenueContext';
import posGovernanceService from '../../../../services/posGovernanceService';
import type {
  PosGovernanceRow,
  PosReconciliationSummary,
  PosReconciliationSummaryIssueSeverity,
  ReconciliationState,
  IssueSeverity,
} from '../../../../services/posGovernanceService';

// ── Helpers ───────────────────────────────────────────────────────────────────

const RECONCILIATION_STATES: ReconciliationState[] = [
  'NONE', 'RESERVED', 'FINALIZED', 'REVERSED', 'INCONSISTENT',
];

/** Stable Phase 6 issue codes from the backend reconciliation contract. */
const KNOWN_ISSUE_CODES: string[] = [
  'DUPLICATE_ACTIVE_RESERVATION',
  'DUPLICATE_SALE_MOVEMENT',
  'DUPLICATE_REVERSAL_MOVEMENT',
  'LINE_CONTEXT_MISSING_FOR_FULL_RECONCILIATION',
  'RESERVATION_WITHOUT_EXECUTION_INSTRUCTION',
  'MOVEMENT_WITHOUT_EXECUTION_INSTRUCTION',
  'MIXED_COMPONENT_STATES',
  'UNEXPECTED_RESERVATION_PRESENT',
  'UNEXPECTED_SALE_MOVEMENT_PRESENT',
  'UNEXPECTED_REVERSAL_MOVEMENT_PRESENT',
  'PARTIAL_RESERVATION_COVERAGE',
  'PARTIAL_SALE_COVERAGE',
  'PARTIAL_REVERSAL_COVERAGE',
  'EXPECTED_RESERVATION_MISSING',
  'EXPECTED_SALE_MISSING',
  'EXPECTED_REVERSAL_MISSING',
];

const stateColor = (
  state: ReconciliationState,
): 'default' | 'info' | 'success' | 'warning' | 'error' => {
  switch (state) {
    case 'FINALIZED': return 'success';
    case 'RESERVED':  return 'info';
    case 'REVERSED':  return 'warning';
    case 'INCONSISTENT': return 'error';
    default:          return 'default';
  }
};

const severityColor = (
  s: IssueSeverity | null,
): 'default' | 'info' | 'warning' | 'error' => {
  switch (s) {
    case 'CRITICAL':
    case 'ERROR':   return 'error';
    case 'WARNING': return 'warning';
    case 'INFO':    return 'info';
    default:        return 'default';
  }
};

const formatDate = (d: string | null | undefined): string => {
  if (!d) return '—';
  return new Date(d).toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const summaryIssueSeverityColor = (
  s: PosReconciliationSummaryIssueSeverity,
): 'info' | 'warning' | 'error' => {
  if (s === 'error') return 'error';
  if (s === 'warning') return 'warning';
  return 'info';
};

// ── Per-row summary cache ─────────────────────────────────────────────────────

interface SummaryCacheEntry {
  loading: boolean;
  data: PosReconciliationSummary | null;
  error: string | null;
}

// ── Section label helper ──────────────────────────────────────────────────────

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="caption"
    fontWeight={700}
    sx={{
      textTransform: 'uppercase',
      letterSpacing: '0.07em',
      color: 'text.disabled',
      display: 'block',
      mb: 1,
    }}
  >
    {children}
  </Typography>
);

// ── Reconciliation summary panel ──────────────────────────────────────────────

const ReconciliationSummaryPanel: React.FC<{ entry: SummaryCacheEntry | undefined }> = ({ entry }) => {
  const theme = useTheme();

  if (!entry || entry.loading) {
    return (
      <Box sx={{ py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">Loading reconciliation summary…</Typography>
      </Box>
    );
  }

  if (entry.error) {
    return <Alert severity="error" sx={{ mt: 1 }}>{entry.error}</Alert>;
  }

  if (!entry.data) return null;

  const s = entry.data;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

      {/* Meta chips */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Chip
          label={`Scope: ${s.scope}`}
          size="small" variant="outlined"
          sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
        />
        <Chip
          label={`Completeness: ${s.reconciliationCompleteness}`}
          size="small" variant="outlined"
          sx={{ fontSize: '0.7rem' }}
        />
        <Chip
          label={s.reconciliationState}
          size="small"
          color={stateColor(s.reconciliationState)}
          sx={{ fontWeight: 600, fontSize: '0.7rem' }}
        />
        <Chip
          label={`${s.issues.length} issue${s.issues.length !== 1 ? 's' : ''}`}
          size="small"
          color={s.issues.length > 0 ? 'warning' : 'default'}
          sx={{ fontSize: '0.7rem' }}
        />
        {s.posMenuItemRef && (
          <Chip
            label={`POS item: ${s.posMenuItemRef}`}
            size="small" variant="outlined"
            sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
          />
        )}
      </Box>

      {/* Expected Set | Active Reservations */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>

        <Box>
          <SectionLabel>Expected Set</SectionLabel>
          <Box sx={{ display: 'flex', gap: 0.75, mb: 1, flexWrap: 'wrap' }}>
            <Chip label={s.expected.mappingMode} size="small" sx={{ fontSize: '0.7rem', fontFamily: 'monospace' }} />
            <Chip
              label={s.expected.instructionPresent ? 'Instruction present' : 'No instruction'}
              size="small"
              color={s.expected.instructionPresent ? 'success' : 'default'}
              sx={{ fontSize: '0.7rem' }}
            />
          </Box>
          {s.expected.expectedKeys.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              <Typography variant="caption" color="text.secondary">
                Expected keys ({s.expected.expectedKeys.length}):
              </Typography>
              {s.expected.expectedKeys.map(k => (
                <Typography key={k} variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                  {k}
                </Typography>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.disabled">No expected components</Typography>
          )}
        </Box>

        <Box>
          <SectionLabel>Active Reservations ({s.activeReservations.length})</SectionLabel>
          {s.activeReservations.length === 0 ? (
            <Typography variant="body2" color="text.disabled">None</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {s.activeReservations.map(r => (
                <Box
                  key={r.reservationId}
                  sx={{
                    p: 1, borderRadius: 1,
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    display: 'flex', flexDirection: 'column', gap: 0.25,
                  }}
                >
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {r.componentKey}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      qty: <strong>{r.quantity}</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(r.createdAt)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>

      </Box>

      {/* Released Reservations | Sale Movements */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>

        <Box>
          <SectionLabel>Released Reservations ({s.releasedReservations.length})</SectionLabel>
          {s.releasedReservations.length === 0 ? (
            <Typography variant="body2" color="text.disabled">None</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {s.releasedReservations.map(r => (
                <Box
                  key={r.reservationId}
                  sx={{
                    p: 1, borderRadius: 1,
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    display: 'flex', flexDirection: 'column', gap: 0.25,
                  }}
                >
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {r.componentKey}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      qty: <strong>{r.quantity}</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      released: {formatDate(r.releasedAt ?? r.updatedAt)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Box>
          <SectionLabel>Sale Movements ({s.saleMovements.length})</SectionLabel>
          {s.saleMovements.length === 0 ? (
            <Typography variant="body2" color="text.disabled">None</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {s.saleMovements.map(m => (
                <Box
                  key={m.movementId}
                  sx={{
                    p: 1, borderRadius: 1,
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    display: 'flex', flexDirection: 'column', gap: 0.25,
                  }}
                >
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {m.componentKey}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      qty: <strong>{m.quantity}</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(m.postedAt)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>

      </Box>

      {/* Reversal Movements (full-width, only when present) */}
      {s.reversalMovements.length > 0 && (
        <Box>
          <SectionLabel>Reversal Movements ({s.reversalMovements.length})</SectionLabel>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {s.reversalMovements.map(m => (
              <Box
                key={m.movementId}
                sx={{
                  p: 1, borderRadius: 1,
                  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                  display: 'flex', flexDirection: 'column', gap: 0.25,
                }}
              >
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip
                    label="SALE_REVERSAL"
                    size="small" color="warning"
                    sx={{ fontSize: '0.65rem', height: 18, fontFamily: 'monospace' }}
                  />
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {m.componentKey}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    qty: <strong>{m.quantity}</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(m.postedAt)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Issues (full-width structured list) */}
      <Box>
        <SectionLabel>Issues ({s.issues.length})</SectionLabel>
        {s.issues.length === 0 ? (
          <Typography variant="body2" color="text.disabled">No issues detected</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {s.issues.map((issue, i) => (
              <Box
                key={i}
                sx={{
                  p: 1.5, borderRadius: 1,
                  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                  backgroundColor: alpha(theme.palette.background.default, 0.5),
                }}
              >
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5, flexWrap: 'wrap' }}>
                  <Chip
                    label={issue.severity}
                    size="small"
                    color={summaryIssueSeverityColor(issue.severity)}
                    sx={{ fontSize: '0.65rem', height: 18, fontWeight: 600 }}
                  />
                  <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {issue.code}
                  </Typography>
                  {issue.componentKey && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                      [{issue.componentKey}]
                    </Typography>
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  {issue.message}
                </Typography>
                {issue.evidence && Object.keys(issue.evidence).length > 0 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 0.75 }}>
                    {Object.entries(issue.evidence).map(([k, v]) => (
                      <Box key={k} sx={{ display: 'flex', gap: 1 }}>
                        <Typography variant="caption" color="text.disabled" sx={{ minWidth: 120 }}>{k}:</Typography>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                          {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>

    </Box>
  );
};

// ── Component ─────────────────────────────────────────────────────────────────

// Leading empty column is for the expand toggle icon
const COLS = [
  '',
  'Source Order Ref',
  'Source Line Ref',
  'State',
  'Completeness',
  'Severity',
  'Needs Attention',
  'Issue Codes',
  'Latest Activity',
];

const rowKey = (row: PosGovernanceRow) =>
  `${row.sourceOrderRef}||${row.sourceLineRef}`;

const PosGovernanceList: React.FC = () => {
  const theme = useTheme();
  const { venueId } = useVenue();

  // ── Pagination ──────────────────────────────────────────────────────────
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  // ── Filters ─────────────────────────────────────────────────────────────
  const [needsAdminAction, setNeedsAdminAction] = useState<'all' | 'yes' | 'no'>('all');
  const [reconciliationState, setReconciliationState] = useState<ReconciliationState | 'all'>('all');
  const [sourceOrderRefInput, setSourceOrderRefInput] = useState('');
  const [sourceOrderRef, setSourceOrderRef] = useState('');
  const [issueCodes, setIssueCodes] = useState<string[]>([]);

  // ── Data ────────────────────────────────────────────────────────────────
  const [rows, setRows] = useState<PosGovernanceRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Row expansion ────────────────────────────────────────────────────────
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const toggleRow = (key: string) =>
    setExpandedKey(prev => (prev === key ? null : key));

  // ── Reconciliation summary cache (fetched on row expand) ─────────────────
  // summaryCacheRef tracks in-flight/completed fetches to prevent duplicates.
  // summaryCache state drives re-renders when data/errors arrive.
  const summaryCacheRef = useRef<Record<string, boolean>>({});
  const [summaryCache, setSummaryCache] = useState<Record<string, SummaryCacheEntry>>({});

  useEffect(() => {
    if (!expandedKey || !venueId) return;
    if (summaryCacheRef.current[expandedKey]) return; // already fetching or fetched
    summaryCacheRef.current[expandedKey] = true;
    const [sourceOrderRef, sourceLineRef] = expandedKey.split('||');
    setSummaryCache(prev => ({ ...prev, [expandedKey]: { loading: true, data: null, error: null } }));
    posGovernanceService.getReconciliationSummary(venueId, sourceOrderRef, sourceLineRef)
      .then(data =>
        setSummaryCache(prev => ({ ...prev, [expandedKey]: { loading: false, data, error: null } })),
      )
      .catch(err =>
        setSummaryCache(prev => ({
          ...prev,
          [expandedKey]: {
            loading: false,
            data: null,
            error: err?.response?.data?.error?.message ?? err?.message ?? 'Failed to load summary',
          },
        })),
      );
  }, [expandedKey, venueId]);

  // ── Per-row summary refresh ───────────────────────────────────────────────
  const refreshSummary = useCallback((key: string) => {
    if (!venueId) return;
    // Clear the in-flight lock so the fetch is allowed to run again
    delete summaryCacheRef.current[key];
    const [sourceOrderRef, sourceLineRef] = key.split('||');
    summaryCacheRef.current[key] = true;
    setSummaryCache(prev => ({ ...prev, [key]: { loading: true, data: null, error: null } }));
    posGovernanceService.getReconciliationSummary(venueId, sourceOrderRef, sourceLineRef)
      .then(data =>
        setSummaryCache(prev => ({ ...prev, [key]: { loading: false, data, error: null } })),
      )
      .catch(err =>
        setSummaryCache(prev => ({
          ...prev,
          [key]: {
            loading: false,
            data: null,
            error: err?.response?.data?.error?.message ?? err?.message ?? 'Failed to load summary',
          },
        })),
      );
  }, [venueId]);

  // ── Repair state ─────────────────────────────────────────────────────────
  const [confirmKey, setConfirmKey] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<'release-duplicates' | 'release-stale' | 'reverse-duplicate-sale' | null>(null);
  const [repairState, setRepairState] = useState<Record<string, { loading: boolean; error: string | null }>>({});

  // ── Debounce sourceOrderRef ──────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => {
      setSourceOrderRef(sourceOrderRefInput);
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [sourceOrderRefInput]);

  // ── Load ────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await posGovernanceService.getGovernanceRows(venueId, {
        limit: pageSize,
        offset: page * pageSize,
        ...(needsAdminAction !== 'all' && { needsAdminAction: needsAdminAction === 'yes' }),
        ...(reconciliationState !== 'all' && { reconciliationState }),
        ...(sourceOrderRef && { sourceOrderRef }),
        ...(issueCodes.length > 0 && { issueCodes }),
      });
      setRows(result.rows);
      setTotal(result.total);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? err?.message ?? 'Failed to load governance data');
    } finally {
      setLoading(false);
    }
  }, [venueId, page, pageSize, needsAdminAction, reconciliationState, sourceOrderRef, issueCodes]);

  useEffect(() => { load(); }, [load]);

  // ── Repair actions ────────────────────────────────────────────────────────
  const runReleaseDuplicateReservations = useCallback(async (key: string) => {
    if (!venueId) return;
    const [orderRef, lineRef] = key.split('||');
    setRepairState(prev => ({ ...prev, [key]: { loading: true, error: null } }));
    try {
      await posGovernanceService.releaseDuplicateReservations(venueId, orderRef, lineRef);
      setRepairState(prev => ({ ...prev, [key]: { loading: false, error: null } }));
      setConfirmKey(null);
      setConfirmAction(null);
      refreshSummary(key);
      load();
    } catch (err: any) {
      setRepairState(prev => ({
        ...prev,
        [key]: {
          loading: false,
          error:
            err?.response?.data?.error?.message ??
            err?.response?.data?.error ??
            err?.message ??
            'Repair failed',
        },
      }));
    }
  }, [venueId, refreshSummary, load]);

  const runReleaseStaleReservations = useCallback(async (key: string) => {
    if (!venueId) return;
    const [orderRef, lineRef] = key.split('||');
    setRepairState(prev => ({ ...prev, [key]: { loading: true, error: null } }));
    try {
      await posGovernanceService.releaseStaleReservations(venueId, orderRef, lineRef);
      setRepairState(prev => ({ ...prev, [key]: { loading: false, error: null } }));
      setConfirmKey(null);
      setConfirmAction(null);
      refreshSummary(key);
      load();
    } catch (err: any) {
      setRepairState(prev => ({
        ...prev,
        [key]: {
          loading: false,
          error:
            err?.response?.data?.error?.message ??
            err?.response?.data?.error ??
            err?.message ??
            'Repair failed',
        },
      }));
    }
  }, [venueId, refreshSummary, load]);

  const runReverseDuplicateSaleMovements = useCallback(async (key: string) => {
    if (!venueId) return;
    const [orderRef, lineRef] = key.split('||');
    setRepairState(prev => ({ ...prev, [key]: { loading: true, error: null } }));
    try {
      await posGovernanceService.reverseDuplicateSaleMovements(venueId, orderRef, lineRef);
      setRepairState(prev => ({ ...prev, [key]: { loading: false, error: null } }));
      setConfirmKey(null);
      setConfirmAction(null);
      refreshSummary(key);
      load();
    } catch (err: any) {
      setRepairState(prev => ({
        ...prev,
        [key]: {
          loading: false,
          error:
            err?.response?.data?.error?.message ??
            err?.response?.data?.error ??
            err?.message ??
            'Repair failed',
        },
      }));
    }
  }, [venueId, refreshSummary, load]);

  const resetPage = () => setPage(0);

  const hasActiveFilters =
    needsAdminAction !== 'all' ||
    reconciliationState !== 'all' ||
    sourceOrderRef !== '' ||
    issueCodes.length > 0;

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>

        {/* Header */}
        <Box sx={{
          px: 3, py: 2.5,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          flexWrap: 'wrap', gap: 1.5,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <PosIcon sx={{ color: theme.palette.primary.main }} />
            <Box>
              <Typography variant="h6" fontWeight={600}>POS Governance</Typography>
              <Typography variant="caption" color="text.secondary">
                Reconciliation status for POS order lines
              </Typography>
            </Box>
          </Box>
          {!loading && (
            <Typography variant="body2" color="text.secondary">
              {total.toLocaleString()} POS order {total === 1 ? 'line' : 'lines'}
            </Typography>
          )}
        </Box>

        {/* Filters */}
        <Box sx={{
          px: 3, py: 2,
          display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          backgroundColor: alpha(theme.palette.background.default, 0.5),
        }}>
          <TextField
            label="Source Order Ref"
            value={sourceOrderRefInput}
            onChange={e => setSourceOrderRefInput(e.target.value)}
            size="small"
            sx={{ width: 220 }}
            placeholder="Filter by order ref…"
          />

          <FormControl size="small" sx={{ minWidth: 168 }}>
            <InputLabel>State</InputLabel>
            <Select
              value={reconciliationState}
              label="State"
              onChange={e => {
                setReconciliationState(e.target.value as ReconciliationState | 'all');
                resetPage();
              }}
            >
              <MenuItem value="all">All States</MenuItem>
              {RECONCILIATION_STATES.map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 168 }}>
            <InputLabel>Needs Attention</InputLabel>
            <Select
              value={needsAdminAction}
              label="Needs Attention"
              onChange={e => {
                setNeedsAdminAction(e.target.value as 'all' | 'yes' | 'no');
                resetPage();
              }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="yes">Yes — needs action</MenuItem>
              <MenuItem value="no">No — no action needed</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Issue Code</InputLabel>
            <Select
              multiple
              value={issueCodes}
              label="Issue Code"
              onChange={e => {
                setIssueCodes(e.target.value as string[]);
                resetPage();
              }}
              renderValue={(selected) =>
                selected.length === 1 ? selected[0] : `${selected.length} codes`
              }
              MenuProps={{ PaperProps: { style: { maxHeight: 320, width: 400 } } }}
            >
              {KNOWN_ISSUE_CODES.map(code => (
                <MenuItem key={code} value={code} dense>
                  <Checkbox
                    size="small"
                    checked={issueCodes.includes(code)}
                    sx={{ py: 0.25, pr: 1 }}
                  />
                  <ListItemText
                    primary={code}
                    slotProps={{
                      primary: { sx: { fontFamily: 'monospace', fontSize: '0.75rem' } },
                    }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {error && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
          </Box>
        )}

        {loading ? (
          <Box sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">Loading governance data…</Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {COLS.map(h => (
                      <TableCell
                        key={h}
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.text.secondary,
                          fontSize: '0.7rem',
                          textTransform: 'uppercase',
                          py: 1.25,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={COLS.length}>
                        <Box sx={{ py: 8, textAlign: 'center', color: theme.palette.text.secondary }}>
                          <PosIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
                          <Typography variant="body2">
                            {hasActiveFilters
                            ? 'No POS order lines match the current filters.'
                            : 'No POS order lines found.'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row, idx) => {
                      const key = rowKey(row);
                      const isExpanded = expandedKey === key;
                      const counts = Object.entries(row.counts ?? {});

                      return (
                        <React.Fragment key={`${key}-${idx}`}>
                          {/* ── Main row ── */}
                          <TableRow
                            hover
                            onClick={() => toggleRow(key)}
                            sx={{
                              cursor: 'pointer',
                              backgroundColor: isExpanded
                                ? alpha(theme.palette.primary.main, 0.04)
                                : undefined,
                            }}
                          >
                            {/* Expand toggle */}
                            <TableCell sx={{ width: 40, pr: 0 }}>
                              <Tooltip title={isExpanded ? 'Collapse' : 'Expand'}>
                                <IconButton size="small" tabIndex={-1} sx={{ pointerEvents: 'none' }}>
                                  {isExpanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            </TableCell>

                            {/* Source Order Ref */}
                            <TableCell>
                              <Typography
                                variant="body2"
                                fontWeight={500}
                                sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                              >
                                {row.sourceOrderRef}
                              </Typography>
                            </TableCell>

                            {/* Source Line Ref */}
                            <TableCell>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                              >
                                {row.sourceLineRef}
                              </Typography>
                            </TableCell>

                            {/* Reconciliation State */}
                            <TableCell>
                              <Chip
                                label={row.reconciliationState}
                                size="small"
                                color={stateColor(row.reconciliationState)}
                                sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                              />
                            </TableCell>

                            {/* Completeness */}
                            <TableCell>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontSize: '0.8rem' }}
                              >
                                {row.reconciliationCompleteness}
                              </Typography>
                            </TableCell>

                            {/* Highest Severity */}
                            <TableCell>
                              {row.highestSeverity ? (
                                <Chip
                                  label={row.highestSeverity}
                                  size="small"
                                  color={severityColor(row.highestSeverity)}
                                  sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                                />
                              ) : (
                                <Typography variant="body2" color="text.disabled">—</Typography>
                              )}
                            </TableCell>

                            {/* Needs Admin Action */}
                            <TableCell>
                              {row.needsAdminAction ? (
                                <Chip
                                  label="Yes"
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                                />
                              ) : (
                                <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.8rem' }}>
                                  —
                                </Typography>
                              )}
                            </TableCell>

                            {/* Issue Codes (compact summary) */}
                            <TableCell>
                              {row.issueCodes.length === 0 ? (
                                <Typography variant="body2" color="text.disabled">—</Typography>
                              ) : (
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', maxWidth: 280 }}>
                                  {row.issueCodes.map(code => (
                                    <Chip
                                      key={code}
                                      label={code}
                                      size="small"
                                      variant="outlined"
                                      sx={{
                                        fontSize: '0.65rem',
                                        height: 20,
                                        fontFamily: 'monospace',
                                        '& .MuiChip-label': { px: 0.75 },
                                      }}
                                    />
                                  ))}
                                </Box>
                              )}
                            </TableCell>

                            {/* Latest Activity */}
                            <TableCell>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                              >
                                {formatDate(row.latestActivityAt)}
                              </Typography>
                            </TableCell>
                          </TableRow>

                          {/* ── Detail row ── */}
                          <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.02) }}>
                            <TableCell
                              colSpan={COLS.length}
                              sx={{ py: 0, border: isExpanded ? undefined : 0 }}
                            >
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ px: 3, py: 2.5 }}>
                                  <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                    gap: 3,
                                  }}>

                                    {/* ── Identity ── */}
                                    <Box>
                                      <Typography
                                        variant="caption"
                                        fontWeight={700}
                                        sx={{ textTransform: 'uppercase', letterSpacing: '0.07em', color: 'text.disabled', display: 'block', mb: 1 }}
                                      >
                                        Identity
                                      </Typography>
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                        <Box>
                                          <Typography variant="caption" color="text.secondary">Source Order Ref</Typography>
                                          <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                                            {row.sourceOrderRef}
                                          </Typography>
                                        </Box>
                                        <Box>
                                          <Typography variant="caption" color="text.secondary">Source Line Ref</Typography>
                                          <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                                            {row.sourceLineRef}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </Box>

                                    {/* ── Status ── */}
                                    <Box>
                                      <Typography
                                        variant="caption"
                                        fontWeight={700}
                                        sx={{ textTransform: 'uppercase', letterSpacing: '0.07em', color: 'text.disabled', display: 'block', mb: 1 }}
                                      >
                                        Status
                                      </Typography>
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 120 }}>
                                            Reconciliation State
                                          </Typography>
                                          <Chip
                                            label={row.reconciliationState}
                                            size="small"
                                            color={stateColor(row.reconciliationState)}
                                            sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                                          />
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 120 }}>
                                            Completeness
                                          </Typography>
                                          <Typography variant="body2">{row.reconciliationCompleteness}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 120 }}>
                                            Highest Severity
                                          </Typography>
                                          {row.highestSeverity ? (
                                            <Chip
                                              label={row.highestSeverity}
                                              size="small"
                                              color={severityColor(row.highestSeverity)}
                                              sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                                            />
                                          ) : (
                                            <Typography variant="body2" color="text.disabled">—</Typography>
                                          )}
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 120 }}>
                                            Needs Admin Action
                                          </Typography>
                                          {row.needsAdminAction ? (
                                            <Chip label="Yes" size="small" color="error" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
                                          ) : (
                                            <Typography variant="body2" color="text.disabled">—</Typography>
                                          )}
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 120 }}>
                                            Latest Activity
                                          </Typography>
                                          <Typography variant="body2">{formatDate(row.latestActivityAt)}</Typography>
                                        </Box>
                                      </Box>
                                    </Box>

                                  </Box>

                                  <Divider sx={{ my: 2, opacity: 0.5 }} />

                                  <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                    gap: 3,
                                  }}>

                                    {/* ── Issue Codes ── */}
                                    <Box>
                                      <Typography
                                        variant="caption"
                                        fontWeight={700}
                                        sx={{ textTransform: 'uppercase', letterSpacing: '0.07em', color: 'text.disabled', display: 'block', mb: 1 }}
                                      >
                                        Issue Codes ({row.issueCodes.length})
                                      </Typography>
                                      {row.issueCodes.length === 0 ? (
                                        <Typography variant="body2" color="text.disabled">No issues</Typography>
                                      ) : (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                          {row.issueCodes.map(code => (
                                            <Typography
                                              key={code}
                                              variant="body2"
                                              sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                                            >
                                              {code}
                                            </Typography>
                                          ))}
                                        </Box>
                                      )}
                                    </Box>

                                    {/* ── Counts ── */}
                                    <Box>
                                      <Typography
                                        variant="caption"
                                        fontWeight={700}
                                        sx={{ textTransform: 'uppercase', letterSpacing: '0.07em', color: 'text.disabled', display: 'block', mb: 1 }}
                                      >
                                        Counts
                                      </Typography>
                                      {counts.length === 0 ? (
                                        <Typography variant="body2" color="text.disabled">—</Typography>
                                      ) : (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                          {counts.map(([k, v]) => (
                                            <Box key={k} sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline' }}>
                                              <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontFamily: 'monospace', fontSize: '0.8rem', minWidth: 180 }}
                                              >
                                                {k}
                                              </Typography>
                                              <Typography variant="body2" fontWeight={700}>
                                                {v}
                                              </Typography>
                                            </Box>
                                          ))}
                                        </Box>
                                      )}
                                    </Box>

                                  </Box>

                                  <Divider sx={{ my: 2, opacity: 0.5 }} />

                                  {/* ── Reconciliation Summary (fetched from Step 1 endpoint) ── */}
                                  <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                      <Typography
                                        variant="caption"
                                        fontWeight={700}
                                        sx={{ textTransform: 'uppercase', letterSpacing: '0.07em', color: 'text.disabled' }}
                                      >
                                        Reconciliation Summary
                                      </Typography>
                                      <Tooltip title="Refresh summary">
                                        <span>
                                          <IconButton
                                            size="small"
                                            onClick={() => refreshSummary(key)}
                                            disabled={summaryCache[key]?.loading}
                                            sx={{ color: 'text.disabled' }}
                                          >
                                            <RefreshIcon sx={{ fontSize: 16 }} />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    </Box>
                                    <ReconciliationSummaryPanel entry={summaryCache[key]} />
                                  </Box>

                                  {/* ── Repair Actions (gated per row) ── */}
                                  {(() => {
                                    const showDuplicates =
                                      row.hasDuplicateActiveReservations === true ||
                                      row.issueCodes.includes('DUPLICATE_ACTIVE_RESERVATION');
                                    const showStale =
                                      row.hasUnexpectedReservations === true ||
                                      row.hasReservationWithoutInstruction === true ||
                                      row.issueCodes.includes('RESERVATION_WITHOUT_EXECUTION_INSTRUCTION') ||
                                      row.issueCodes.includes('UNEXPECTED_RESERVATION_PRESENT');
                                    const showSaleDuplicates =
                                      row.hasDuplicateSaleMovements === true ||
                                      row.issueCodes.includes('DUPLICATE_SALE_MOVEMENT');
                                    if (!showDuplicates && !showStale && !showSaleDuplicates) return null;
                                    return (
                                      <>
                                        <Divider sx={{ my: 2, opacity: 0.5 }} />
                                        <Box>
                                          <Typography
                                            variant="caption"
                                            fontWeight={700}
                                            sx={{ textTransform: 'uppercase', letterSpacing: '0.07em', color: 'text.disabled', display: 'block', mb: 1.5 }}
                                          >
                                            Repair Actions
                                          </Typography>
                                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            {showDuplicates && (
                                              <Button
                                                variant="outlined"
                                                color="warning"
                                                size="small"
                                                disabled={repairState[key]?.loading}
                                                startIcon={repairState[key]?.loading
                                                  ? <CircularProgress size={14} color="inherit" />
                                                  : undefined}
                                                onClick={() => { setConfirmKey(key); setConfirmAction('release-duplicates'); }}
                                                sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' }}
                                              >
                                                Release Duplicate Reservations
                                              </Button>
                                            )}
                                            {showStale && (
                                              <Button
                                                variant="outlined"
                                                color="warning"
                                                size="small"
                                                disabled={repairState[key]?.loading}
                                                startIcon={repairState[key]?.loading
                                                  ? <CircularProgress size={14} color="inherit" />
                                                  : undefined}
                                                onClick={() => { setConfirmKey(key); setConfirmAction('release-stale'); }}
                                                sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' }}
                                              >
                                                Release Stale Reservations
                                              </Button>
                                            )}
                                            {showSaleDuplicates && (
                                              <Button
                                                variant="outlined"
                                                color="warning"
                                                size="small"
                                                disabled={repairState[key]?.loading}
                                                startIcon={repairState[key]?.loading
                                                  ? <CircularProgress size={14} color="inherit" />
                                                  : undefined}
                                                onClick={() => { setConfirmKey(key); setConfirmAction('reverse-duplicate-sale'); }}
                                                sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' }}
                                              >
                                                Reverse Duplicate Sale Movements
                                              </Button>
                                            )}
                                          </Box>
                                        </Box>
                                      </>
                                    );
                                  })()}

                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={total}
              page={page}
              rowsPerPage={pageSize}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={e => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          </>
        )}
      </Paper>

      {/* ── Repair confirmation dialog (shared, action-aware) ── */}
      <Dialog
        open={confirmKey !== null}
        onClose={() => {
          if (!repairState[confirmKey ?? '']?.loading) {
            setConfirmKey(null);
            setConfirmAction(null);
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {confirmAction === 'release-stale'
            ? 'Release Stale Reservations'
            : confirmAction === 'reverse-duplicate-sale'
              ? 'Reverse Duplicate Sale Movements'
              : 'Release Duplicate Reservations'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {confirmAction === 'release-stale'
              ? <>
                  This will release stale <strong>ACTIVE</strong> reservations for this line —
                  reservations that exist without a valid execution instruction or are unexpected
                  given the current reconciliation state. Released reservations cannot be restored.
                </>
              : confirmAction === 'reverse-duplicate-sale'
                ? <>
                    This will post compensating <strong>SALE_REVERSAL</strong> movements for
                    extra duplicate SALE movements on this line. One SALE movement per component
                    key is kept; the rest are reversed. This is a ledger write and cannot be undone.
                  </>
                : <>
                    This will release all duplicate <strong>ACTIVE</strong> reservations for this line,
                    keeping one survivor per component key (earliest by creation time).
                    Released reservations cannot be restored.
                  </>}
          </Typography>
          {confirmKey && repairState[confirmKey]?.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {repairState[confirmKey].error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => { setConfirmKey(null); setConfirmAction(null); }}
            disabled={!!confirmKey && repairState[confirmKey]?.loading}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={!!confirmKey && repairState[confirmKey]?.loading}
            startIcon={!!confirmKey && repairState[confirmKey]?.loading
              ? <CircularProgress size={14} color="inherit" />
              : undefined}
            onClick={() => {
              if (!confirmKey) return;
              if (confirmAction === 'release-stale') runReleaseStaleReservations(confirmKey);
              else if (confirmAction === 'reverse-duplicate-sale') runReverseDuplicateSaleMovements(confirmKey);
              else runReleaseDuplicateReservations(confirmKey);
            }}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {!!confirmKey && repairState[confirmKey]?.loading
              ? (confirmAction === 'reverse-duplicate-sale' ? 'Reversing…' : 'Releasing…')
              : (confirmAction === 'reverse-duplicate-sale' ? 'Reverse Movements' : 'Release')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PosGovernanceList;
